import { type UNSAFE_any } from '@flecto/types'

export const pick = (
  object: Record<string, UNSAFE_any>,
  keys: Array<string>
): Record<string, UNSAFE_any> => {
  try {
    return Object.fromEntries(
      keys
        .filter((key: UNSAFE_any) => key in object)
        .map((key: UNSAFE_any) => [key, object[key]])
    )
  } catch {
    return {}
  }
}

export const omit = (
  object: Record<string, UNSAFE_any>,
  keys: Array<string>
): Record<string, UNSAFE_any> => {
  if (!object) return {}
  try {
    const payload = Object.entries(object).filter(
      ([key]) => !keys.includes(key)
    )
    const data: { [key: string]: UNSAFE_any } = {}

    payload.forEach((item) => {
      const [key, value] = item
      data[key] = value
    })
    return data
  } catch {
    return {}
  }
}

const isObject = (object: UNSAFE_any) => {
  return object != null && typeof object === 'object'
}

export const isEqual = (obj1: UNSAFE_any, obj2: UNSAFE_any) => {
  try {
    const props1 = Object.getOwnPropertyNames(obj1)
    const props2 = Object.getOwnPropertyNames(obj2)
    if (props1.length !== props2.length) {
      return false
    }
    for (let i = 0; i < props1.length; i++) {
      const val1 = obj1[props1[i]]
      const val2 = obj2[props1[i]]
      const isObjects = isObject(val1) && isObject(val2)
      if (
        (isObjects && !isEqual(val1, val2)) ||
        (!isObjects && val1 !== val2)
      ) {
        return false
      }
    }
    return true
  } catch {
    return false
  }
}

export const detectArrayElementDifferences = <
  T extends string | Record<string, unknown>,
>(
  arr1: T[] = [],
  arr2: T[] = []
): boolean => {
  if (arr1.length !== arr2.length) return true
  return arr1.some((obj1, index) => {
    const obj2 = arr2[index]
    if (typeof obj1 === 'string' || typeof obj2 === 'string') {
      return obj1 !== obj2
    }
    return Object.keys(obj1).some(
      (key) =>
        (obj1 as Record<string, unknown>)[key] !==
        (obj2 as Record<string, unknown>)[key]
    )
  })
}
